import {
  Feature,
  featureCollection,
  FeatureCollection,
  Point,
  Position,
} from '@turf/helpers'
import { Extent } from 'ol/extent'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ImageMapContainer } from '../../ui/shared/individual-images/image-view'
import useResizableSplit from '../../../hooks/use-resizable-split'
import bbox from '@turf/bbox'
import { createOverviewStyle, markerStyle } from './ol-styles'
import useMiniMap from '../../ui/shared/individual-images/use-mini-map'
import ImagesMap from '../../ui/shared/individual-images/images-map'
import GeotaggedImageLayer from '../../geotagged_image_layer'
import { maxImageZoom, zIndexes } from '../../../ol-styles'
import FeatureSelect from '../../feature-select'
import { Projection, transformExtent } from 'ol/proj'
import useImage from '../../../hooks/use-image'
import ImageLayer from '../../ol/image-layer'
import { Collection, Feature as OlFeature, Map, View } from 'ol'
import * as olExtent from 'ol/extent'
import GreenToggle from '../../ui/elements/green-toggle'
import { Point as OlPoint } from 'ol/geom'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Modify from 'ol/interaction/Modify'
import Style from 'ol/style/Style'
import { Image, Gcp } from './types'
import thumbnail from './thumbnail'

type MapViewProps = {
  images: FeatureCollection<Point>
  gcps: FeatureCollection<Point>
  selectedImage?: Image
  selectedImageFile?: File
  selectedGcp?: Gcp
  includeMarker?: boolean
  markerCoordinates?: Position
  mapboxToken: string
  onSelect: (feature: Feature) => void
  updateMarker?: (
    includeMarker: boolean,
    coordinates: Position,
    thumbnailUrl: string | undefined
  ) => void
}

export default function MapView({
  images,
  gcps,
  selectedImage,
  selectedImageFile,
  selectedGcp,
  mapboxToken,
  includeMarker,
  markerCoordinates,
  onSelect,
  updateMarker,
}: MapViewProps) {
  const mapFeatures = useMemo(
    () =>
      featureCollection([
        ...gcps.features.map(f => ({
          ...f,
          properties: { ...f.properties, type: 'gcp' },
        })),
        ...images.features.map(f => ({
          ...f,
          properties: { ...f.properties, type: 'image' },
        })),
      ]),
    [images, gcps]
  )
  const selection = useMemo(
    () =>
      featureCollection(
        [selectedGcp, selectedImage].filter(Boolean) as Feature<Point>[]
      ),
    [selectedGcp, selectedImage]
  )

  const unselectedMarkerStyle = useMemo(
    () => createOverviewStyle(false, selectedGcp?.properties.id),
    [selectedGcp]
  )
  const selectedMarkerStyle = useMemo(
    () => createOverviewStyle(true, selectedGcp?.properties.id),
    [selectedGcp]
  )
  const extent = useMemo(() => bbox(images), [images])
  const {
    onDragStart,
    onDrag,
    onDragEnd,
    splitHeight: overviewMapHeight,
  } = useResizableSplit(320)
  const getImageData = useCallback(() => selectedImageFile, [selectedImageFile])
  const { loadedImage: image, status, error, extent: imageExtent } = useImage(
    selectedImage?.properties.originalImageFeature,
    getImageData
  )
  const projection = useMemo(
    () =>
      new Projection({
        code: 'drone-image',
        units: 'pixels',
        extent: imageExtent,
      }),
    [imageExtent]
  )
  const [map, setMap] = useState<Map>()
  const width = image?.width
  const height = image?.height
  useEffect(() => {
    if (map && imageExtent && width && height) {
      const view = new View({
        projection: projection,
        center: olExtent.getCenter(imageExtent),
        extent: olExtent.buffer(imageExtent, Math.max(width, height) * 0.05),
        zoom: 2,
        minZoom: 2,
        maxZoom: maxImageZoom,
      })
      map.setView(view)
    }
  }, [imageExtent, width, height, projection, map])

  const currentMarkerCoordinates =
    markerCoordinates || (imageExtent && olExtent.getCenter(imageExtent))
  useEffect(() => {
    if (map && imageExtent) {
      const marker = new OlFeature(new OlPoint(currentMarkerCoordinates))
      const source = new VectorSource({ features: [marker] })
      const layer = new VectorLayer({ source, style: markerStyle })
      const translate = new Modify({
        features: new Collection([marker]),
        style: new Style(),
      })

      translate.on('modifyend', e => {
        const coordinates = (e.features
          .item(0)
          .getGeometry() as OlPoint)?.getCoordinates()
        if (coordinates) {
          const thumbnailUrl = thumbnail(image, coordinates)
          updateMarker && updateMarker(true, coordinates, thumbnailUrl)
        }
      })

      map.addLayer(layer)
      map.addInteraction(translate)

      return () => {
        map.removeInteraction(translate)
        map.removeLayer(layer)
      }
    }
  }, [map, currentMarkerCoordinates, imageExtent, updateMarker, image])

  return (
    <div
      className="tw-w-full tw-h-full"
      onPointerMove={onDrag}
      onPointerUp={onDragEnd}>
      <div className="tw-w-full tw-h-full tw-flex tw-flex-col">
        <div className="tw-w-full tw-h-full tw-flex-grow-1">
          <ImageMapContainer>
            {selectedImage && (
              <>
                <ImagesMap
                  filename={
                    selectedImage.properties?.originalImageFeature.properties
                      ?.filename
                  }
                  status={status}
                  info={
                    <GreenToggle
                      active={includeMarker}
                      onChange={e =>
                        updateMarker &&
                        updateMarker(
                          e.target.checked,
                          currentMarkerCoordinates,
                          e.target.checked
                            ? thumbnail(image, currentMarkerCoordinates)
                            : undefined
                        )
                      }>
                      Include Marker
                    </GreenToggle>
                  }
                  onMapInitialized={setMap}
                  error={error}
                />
                {map && image && imageExtent && (
                  <ImageLayer
                    map={map}
                    imageUrl={image.src}
                    projection={projection}
                    extent={imageExtent}
                  />
                )}
              </>
            )}
          </ImageMapContainer>
        </div>
        <div
          className="tw-w-full tw-h-5 hover:tw-bg-gray-100 tw-select-none tw-text-center tw-text-gray-300 tw-transition tw-transition-colors"
          onPointerDown={onDragStart}>
          <i className="fas fa-grip-lines" />
        </div>
        <div
          style={{ height: overviewMapHeight }}
          className="tw-w-full tw-flex-shrink-0 tw-resize-y">
          <OverviewMap
            extent={extent}
            mapboxToken={mapboxToken}
            features={mapFeatures}
            selectedFeatures={selection}
            onSelectFeature={onSelect}
            style={unselectedMarkerStyle}
            selectedStyle={selectedMarkerStyle}
          />
        </div>
      </div>
    </div>
  )
}

type OverviewMapProps = {
  extent: Extent
  features: FeatureCollection<Point>
  selectedFeatures: FeatureCollection<Point>
  onSelectFeature: (feature: Feature<Point>) => void
  style: any
  selectedStyle: any
  mapboxToken: string
}

function OverviewMap({
  extent,
  mapboxToken,
  features,
  selectedFeatures,
  onSelectFeature,
  style,
  selectedStyle,
}: OverviewMapProps) {
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | undefined>(
    undefined
  )
  const epsg3857Extent = useMemo(
    () => transformExtent(extent, 'EPSG:4326', 'EPSG:3857'),
    [extent]
  )
  const miniMap = useMiniMap({
    container: mapContainer,
    mapboxToken,
    extent: epsg3857Extent,
  })

  const [geotaggedLayer, setGeotaggedLayer] = useState(null)
  useItemInView(miniMap, selectedFeatures)

  return (
    <>
      <div
        className="tw-w-full tw-h-full"
        ref={node => setMapContainer(node || undefined)}></div>
      {miniMap && (
        <>
          <GeotaggedImageLayer
            map={miniMap}
            geojson={features}
            style={style}
            zIndex={zIndexes.sourceImagery}
            onLayerCreated={(layer: any) => setGeotaggedLayer(layer)}
            onLayerRemoved={() => {
              setGeotaggedLayer(null)
            }}
          />
          <FeatureSelect
            map={miniMap}
            layers={geotaggedLayer ? new Set([geotaggedLayer]) : []}
            selection={selectedFeatures}
            featureIdProperty="id"
            style={selectedStyle}
            onSelect={({ features }: FeatureCollection<Point>) =>
              features.length > 0 && onSelectFeature(features[0])
            }
            preventDeselect={true}
          />
        </>
      )}
    </>
  )
}

function useItemInView(map: Map | undefined, features: FeatureCollection) {
  useEffect(() => {
    if (map && features.features.length) {
      const featureBbox = bbox(features)
      const epsg3857Bbox = transformExtent(
        featureBbox,
        'EPSG:4326',
        'EPSG:3857'
      )
      const viewExtent = map.getView().calculateExtent()
      if (!olExtent.intersects(epsg3857Bbox, viewExtent)) {
        map.getView().fit(epsg3857Bbox, {
          maxZoom: 18,
          duration: 250,
          padding: [40, 40, 40, 40],
        })
      }
    }
  }, [map, features])
}
